/**
 * « Des tours de l'assistant sont en cours, et voici sur quoi ils portent. »
 *
 * Le canevas doit montrer OÙ l'attente se joue — un carré de chargement à
 * l'emplacement cliqué, un bloc grisé pendant qu'on le modifie —, mais les tours
 * appartiennent au panneau de chat : c'est lui qui tient le flux attaché et la
 * liste sondée. Ce module est le seul fil entre les deux.
 *
 * Un slot de portée module + abonnés, comme `blockPlacement` : le panneau et le
 * canevas ne partagent aucun contexte React (le panneau est monté par
 * `BuilderShell`, hors de l'arbre du canevas), et un événement `window` seul
 * obligerait chaque abonné à retenir le dernier état pour le lire au montage —
 * ce que fait ici `getChatTurns`, une fois pour tous.
 *
 * **Une LISTE, pas un tour.** L'état a d'abord été `{active, target}` : un seul
 * bloc pouvait paraître au travail, et il s'éteignait dès qu'on quittait sa
 * conversation ou qu'on en lançait une seconde — alors que le tour, lui,
 * continuait côté sidecar. Le panneau publie donc TOUS les tours vivants qu'il
 * connaît : celui dont il tient le flux, et ceux que `/chat/runs` lui rend (dont
 * la cible voyage désormais avec le tour, cf. services/claude-agent/src/runs.js).
 *
 * L'état reste DÉLIBÉRÉMENT minimal : de quoi décider quel voile afficher, jamais
 * le contenu d'un tour. Le rendu du résultat reste le chemin existant
 * (`builder:registry:invalidate` → rechargement des défs touchées).
 *
 * @typedef {object} ChatTurn
 * @property {string}  conversationId
 * @property {object?} target   — le bloc épinglé, ou `null`
 * @property {'running'|'queued'} status
 * @property {boolean} watched  — l'écran tient-il le flux de ce tour ?
 */

import { useSyncExternalStore } from 'react'

/** @type {ChatTurn[]} */
const NONE = []

let turns = NONE
const listeners = new Set()

const notify = () => { for (const fn of listeners) fn() }

/**
 * Ce qui distingue deux listes de tours POUR CE QU'ON EN FAIT : qui travaille,
 * sur quoi, et dans quel état.
 *
 * Le sondage reconstruit un tableau neuf toutes les dix secondes ; comparer les
 * références re-rendrait alors chaque bloc de la page à chaque sondage, sans que
 * rien n'ait bougé.
 */
const signature = (list) => list
  .map(t => [
    t.conversationId,
    t.status,
    t.watched ? '1' : '',
    t.target?.intent ?? '',
    t.target?.blocId ?? '',
    t.target?.type ?? '',
  ].join('|'))
  .sort()
  .join(',')

/**
 * Publié par le panneau de chat à chaque changement de sa liste de tours vivants.
 * Idempotent : republier une liste équivalente ne réveille personne (un effet
 * React se rejoue plus souvent qu'il ne change quelque chose).
 *
 * @param {Array<{conversationId: string, target?: object|null, status?: string, watched?: boolean}>} next
 */
export const publishTurnStatus = (next) => {
  const list = (next ?? []).filter(t => t && t.conversationId)
    .map(t => ({
      conversationId: t.conversationId,
      target: t.target ?? null,
      // Un tour en file n'a pas commencé. Le dire, plutôt que d'annoncer une
      // « modification en cours » que l'éditeur peut démentir tout seul en
      // ouvrant l'assistant (« En attente d'une place (2e) »).
      status: t.status === 'queued' ? 'queued' : 'running',
      watched: Boolean(t.watched),
    }))
  if (list.length === 0 && turns.length === 0) return
  if (signature(list) === signature(turns)) return
  turns = list.length === 0 ? NONE : list
  notify()
}

export const getChatTurns = () => turns

export const subscribeTurnStatus = (fn) => {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** Les tours en cours, re-rendus à chaque changement. */
export const useChatTurns = () => useSyncExternalStore(subscribeTurnStatus, getChatTurns, getChatTurns)

/**
 * Les conversations qui ont un tour EN VIE, ici et maintenant.
 *
 * C'est ce qui allume — et surtout ce qui éteint — les places réservées : chaque
 * demande d'ajout nomme la conversation qui doit la remplir (cf.
 * `blockPlacement`), et son carré d'attente ne tient que tant que cette
 * conversation-là travaille. Un tour d'un autre onglet ou d'une autre page ne
 * porte donc aucune des demandes de cet écran, et n'en rallume aucune.
 *
 * Remplace un ancien prédicat global (« un tour peut-il poser quelque chose ? »)
 * qui ne pouvait pas dire LEQUEL : avec une seule place réservée à la fois, la
 * question ne se posait pas ; avec plusieurs, elle est toute la réponse.
 *
 * @param {ChatTurn[]} list
 * @returns {Set<string>}
 */
export const liveConversationIds = (list) => new Set(
  (list ?? []).map(turn => turn.conversationId).filter(Boolean),
)

/**
 * Dans quel état est le tour qui porte sur CE bloc de la page — s'il y en a un ?
 *
 * Seule l'intention `edit` vise un bloc existant : une création ne grise rien,
 * elle fait apparaître un carré ailleurs. On compare d'abord sur l'instance
 * (`blocId`), qui distingue deux blocs du même type sur la même page ; à défaut —
 * la cible vient du catalogue, où il n'y a pas d'instance — sur le type, et
 * toutes les instances de ce type s'assombrissent, ce qui est exact : c'est la
 * définition qui va changer.
 *
 * Une intention ABSENTE ne vaut pas `edit` : une charge malformée griserait
 * sinon toutes les instances d'un type sans que rien ne le demande.
 *
 * @param {ChatTurn[]} list
 * @param {{id?: string, type?: string}} bloc
 * @returns {'running'|'queued'|null}
 */
export const blocTurnStatus = (list, bloc) => {
  if (!bloc || !Array.isArray(list)) return null
  const hit = list.find(({ target }) => {
    if (!target || target.intent !== 'edit') return false
    if (target.blocId) return target.blocId === bloc.id
    return Boolean(target.type) && target.type === bloc.type
  })
  return hit ? hit.status : null
}
